import { useMutation, useQuery } from '@apollo/client';
import { KeyboardArrowDownRounded } from '@material-ui/icons';
import { FormikProvider, useFormik } from 'formik';
import {
  AdminRequestUpdateInput,
  AdminUpdateRequestMutation,
  AdminUpdateRequestMutationVariables,
  Admin_Get_UsersQuery,
  Admin_Get_UsersQueryVariables,
  Request,
  RequestStatus,
  User,
} from 'lib/generated/graphql';
import { ADMIN_UPDATE_REQUEST } from 'lib/mutations';
import { ADMIN_FIND_REQUESTS, ADMIN_GET_USERS } from 'lib/queries';
import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';
import Avatar from 'src/shared-components/Avatar';
import Button from 'src/shared-components/Button';
import { InputField, Select, TextArea } from 'src/shared-components/Form';
import Modal from 'src/shared-components/Modal';
import Title from 'src/shared-components/Modal/Title';
import * as Yup from 'yup';

const DEFAULT_USER_AVATAR = process.env.DEFAULT_USER_AVATAR || '';

const SetPartner = ({
  request,
  setIsLoading,
  closeModal,
}: {
  request: Request;
  setIsLoading?: (isLoading: boolean) => void;
  closeModal?: () => void;
}) => {
  const [updateRequest] = useMutation<AdminUpdateRequestMutation, AdminUpdateRequestMutationVariables>(
    ADMIN_UPDATE_REQUEST,
    {
      refetchQueries: [ADMIN_FIND_REQUESTS],
    },
  );
  const { data: findUserData } = useQuery<Admin_Get_UsersQuery, Admin_Get_UsersQueryVariables>(ADMIN_GET_USERS, {
    variables: {
      // adminGetUsersOrderBy: [
      //   {
      //     isPartner: SortOrder.Desc,
      //   },
      // ],
      adminGetUsersWhere: {
        isPartner: {
          equals: true,
        },
      },
    },
  });

  const form = useFormik({
    initialValues: {
      partnerId: request?.partnerId,
    },
    enableReinitialize: true,
    onSubmit: async values => {
      setIsLoading(true);
      const inputData: AdminRequestUpdateInput = {
        partner: {
          // @ts-ignore
          connect: { id: parseInt(values.partnerId, 10) },
        },
      };
      await updateRequest({
        variables: {
          input: inputData,
          where: {
            id: request.id,
          },
        },
      });
      setIsLoading(false);
      closeModal();
    },
  });

  const { submitForm } = form;

  return (
    <FormikProvider value={form}>
      <Select
        label="PARTNER"
        options={
          findUserData?.adminGetUsers?.users?.map((user: User) => ({
            label: user.name,
            value: user.id,
          })) || []
        }
        name="partnerId"
      />
      <div className="mt-4">
        <Button color="black" size="lg" fullWidth onClick={submitForm}>
          <span className="font-bold">Submit</span>
        </Button>
      </div>
    </FormikProvider>
  );
};

const PartnerStatusForm = ({
  request,
  setIsLoading,
  closeModal,
}: {
  request: Request;
  setIsLoading?: (isLoading: boolean) => void;
  closeModal?: () => void;
}) => {
  const [validationSchema, setValidationSchema] = useState<Yup.ObjectSchema>(Yup.object().shape({}));
  const [updateRequest] = useMutation<AdminUpdateRequestMutation, AdminUpdateRequestMutationVariables>(
    ADMIN_UPDATE_REQUEST,
    {
      refetchQueries: [ADMIN_FIND_REQUESTS],
    },
  );
  const { data: findUserData } = useQuery<Admin_Get_UsersQuery, Admin_Get_UsersQueryVariables>(ADMIN_GET_USERS, {
    variables: {
      // adminGetUsersOrderBy: [
      //   {
      //     isPartner: SortOrder.Desc,
      //   },
      // ],
      adminGetUsersWhere: {
        isPartner: {
          equals: true,
        },
      },
    },
  });

  const form = useFormik({
    initialValues: {
      partnerNotes: request.partnerNotes,
      status: request.status,
      partnerId: request?.partnerId,
      roomLink: request?.roomLink ? request.roomLink : '',
    },
    validationSchema: validationSchema,
    enableReinitialize: true,
    onSubmit: async values => {
      setIsLoading(true);
      const inputData: AdminRequestUpdateInput = {
        partnerNotes: {
          set: values.partnerNotes,
        },
        status: {
          set: values.status,
        },
      };
      if (values.partnerId) {
        inputData.partner = {
          // @ts-ignore
          connect: { id: parseInt(values.partnerId, 10) },
        };
      }
      if (values.roomLink) {
        inputData.roomLink = {
          set: values.roomLink,
        };
      }
      await updateRequest({
        variables: {
          input: inputData,
          where: {
            id: request.id,
          },
        },
      });
      setIsLoading(false);
      closeModal();
    },
  });

  const { values, setFieldValue, submitForm } = form;

  useEffect(() => {
    if (values.status === RequestStatus.Approved) {
      setValidationSchema(
        Yup.object().shape({
          roomLink: Yup.string()
            .required('A Slack Room Link Is Required')
            .notOneOf(['', null], 'A Slack Room Link Is Required'),
        }),
      );
    } else {
      setValidationSchema(Yup.object().shape({}));
    }
  }, [values.status]);

  return (
    <div className="min-w-[450px] max-w-[450px]">
      <FormikProvider value={form}>
        <Title title="Update review status" />
        <div className="flex flex-col gap-y-3">
          <Button
            fullWidth
            color={values.status === RequestStatus.PendingReview ? 'primary' : 'secondary'}
            onClick={() => setFieldValue('status', RequestStatus.PendingReview)}
          >
            <span>Pending Review</span>
          </Button>
          <Button
            fullWidth
            color={values.status === RequestStatus.Approved ? 'primary' : 'secondary'}
            onClick={() => setFieldValue('status', RequestStatus.Approved)}
          >
            <span>Approved</span>
          </Button>
          <Button
            fullWidth
            color={values.status === RequestStatus.Denied ? 'primary' : 'secondary'}
            onClick={() => setFieldValue('status', RequestStatus.Denied)}
          >
            <span>Denied</span>
          </Button>
          <Button
            fullWidth
            color={values.status === RequestStatus.ClarificationNeeded ? 'primary' : 'secondary'}
            onClick={() => setFieldValue('status', RequestStatus.ClarificationNeeded)}
          >
            <span>Clarification Needed</span>
          </Button>
        </div>

        <div className="my-5">
          {values.status === RequestStatus.Approved ? (
            <div className="flex flex-col gap-y-4">
              <InputField label="ADD ROOM LINK" required placeholder="https://slack.com/roomkey" name="roomLink" />
              <Select
                label="PARTNER"
                options={
                  findUserData?.adminGetUsers?.users?.map((user: User) => ({
                    label: user.name,
                    value: user.id,
                  })) || []
                }
                name="partnerId"
              />
              <p className="max-w-[450px] text-[#838383] text-sm">
                Note: When you hit Submit, the requester will be notified that their Request was approved and will be
                prompted to jump into their Huddle Room, text the Partner or schedule a call.
              </p>
            </div>
          ) : (
            <TextArea
              name="partnerNotes"
              label="ADD NOTE TO REQUESTER (OPTIONAL)"
              placeholder={
                values.status === RequestStatus.Denied
                  ? "Common rejection reasons include your company being funded less than $250k or requesting a service we don't support. If you have any questions, please email us at support@huddle.works."
                  : 'Add a note on why you changed the Request status. Strongly suggested for Requests that are Denied or Clarification needed.'
              }
            />
          )}
        </div>

        <Button color="black" size="lg" fullWidth onClick={submitForm}>
          <span className="font-bold">Submit</span>
        </Button>
      </FormikProvider>
    </div>
  );
};

const PartnerHeader = ({ request }: { request: Request }) => {
  const statusModalRef = useRef<any>();
  const partnerModalRef = useRef<any>();

  const renderStatus = () => {
    switch (request.status) {
      case RequestStatus.PendingReview:
        return 'Pending Review';
      case RequestStatus.ClarificationNeeded:
        return 'Clarification Needed';
      case RequestStatus.Denied:
        return 'Denied';
      default:
        return null;
    }
  };

  return (
    <div className="bg-white flex flex-col  p-6 rounded-tl-xl rounded-tr-xl drop-shadow">
      <div className="flex flex-row items-center justify-between">
        <div className="flex flex-row space-x-5">
          <div className="relative">
            <Avatar
              size={'xs'}
              roundness={'md'}
              image={request.company.customer.photoUrl || DEFAULT_USER_AVATAR}
              username={request.company.customer.username}
            />
            {request?.company?.customer?.inviterId && (
              <div className="h-7 w-7 absolute top-2/4 left-2/4  hover:opacity-80">
                <Link href={`/${request?.company?.customer?.inviter?.username}`}>
                  <a>
                    <Image
                      className="rounded-[10px]"
                      layout="fill"
                      src={request?.company?.customer?.inviter?.photoUrl || DEFAULT_USER_AVATAR}
                    />
                  </a>
                </Link>
              </div>
            )}
          </div>
          <div className="flex flex-col gap-y-1">
            <p className="font-bold text-base">{request?.company?.customer?.name}</p>
            <span className="text-[#595959] font-medium text-sm">{request?.company?.customer?.bio}</span>
          </div>
        </div>
        <div className="flex flex-row gap-x-4">
          <Button color="white" size="lg" onClick={() => partnerModalRef?.current?.open()}>
            <div className="flex flex-row items-center gap-x-3">
              <KeyboardArrowDownRounded />
              <span className="font-bold">Set Partner</span>
            </div>
          </Button>
          <Button color="white" size="lg" onClick={() => statusModalRef?.current?.open()}>
            <div className="flex flex-row items-center gap-x-3">
              <KeyboardArrowDownRounded />
              <span className="font-bold"> {renderStatus()}</span>
            </div>
          </Button>
        </div>
      </div>
      <Modal ref={statusModalRef} showCloseButton>
        <PartnerStatusForm request={request} closeModal={() => statusModalRef?.current?.close()} />
      </Modal>
      <Modal ref={partnerModalRef} showCloseButton>
        <SetPartner request={request} closeModal={() => partnerModalRef?.current?.close()} />
      </Modal>
    </div>
  );
};

export default PartnerHeader;
